import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../app/hooks";
import { ActionBanner } from "../../common/components/functional/actionBanner/ActionBanner";
import {
  ActionButton,
  ActionButtonGroup,
  GroupButton,
} from "../../common/components/functional/actionBanner/styled";
import { InvoiceDisplay, InvoiceWavePost } from "./invoiceAPI";
import { changeInvoiceWave, sendInvoices } from "./invoiceSlice";
import {
  INVOICE_WAVE_ONE,
  INVOICE_WAVE_TWO,
  INVOICE_READY,
  INVOICE_SENT_SUCCESS,
  INVOICE_SENT_FAILED,
  INVOICE_DRAFT,
  INVOICE_EDITED,
} from "./constants";
import useMessage from "../../hooks/useMessage";
import { ConfirmationDialog } from "../../common/components/functional/confirmationDialog/ConfirmationDialog";
import usePreviewInvoices from "./hooks/usePreviewInvoices";

interface Props {
  translation: string;
  invoices: InvoiceDisplay[];
  selectedInvoices: InvoiceDisplay[];
  openCreditInvoiceDialog: (invoiceId: string) => void;
  isUserAllowedToEdit: boolean;
}

export const InvoiceActionBanner = ({
  translation,
  invoices,
  selectedInvoices,
  openCreditInvoiceDialog,
  isUserAllowedToEdit,
}: Props) => {
  const dispatch = useAppDispatch();
  const previewInvoices = usePreviewInvoices();
  const { sendMessage } = useMessage();
  const { t } = useTranslation(translation);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(
    new Set(),
  );
  const [selectedWaves, setSelectedWaves] = useState<Set<string | undefined>>(
    new Set(),
  );

  const [canMarkFirstWave, setCanMarkFirstWave] = useState<boolean>(false);
  const [canMarkSecondWave, setCanMarkSecondWave] = useState<boolean>(false);
  const [canSend, setCanSend] = useState<boolean>(false);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [canCreateCreditInvoice, setCanCreateCreditInvoice] =
    useState<boolean>(false);

  const previewSelected = () => {
    previewInvoices(selectedInvoices.map((invoice) => invoice.invoiceNumber));
  };

  const markWave = (wave: string) => {
    selectedInvoices.forEach((selectedInvoice) => {
      const invoiceWaveInfo: InvoiceWavePost = {
        version: selectedInvoice.version!!,
        invoiceId: selectedInvoice.id,
        invoiceSentWave: wave,
      };

      dispatch(changeInvoiceWave(invoiceWaveInfo)).then((action) => {
        if (isFulfilled(action)) {
          sendMessage(t("waveUpdated"), "success");
        }
      });
    });
  };

  const sendSelectedInvoices = () => {
    const ids = selectedInvoices.map((invoice) => invoice.id);
    dispatch(sendInvoices(ids));
    setIsConfirmationDialogOpen(false);
  };

  const askForConfirmation = () => {
    setIsConfirmationDialogOpen(true);
  };

  useEffect(() => {
    // If selected invoices have multiple states, prevent sending
    // (except if they are in the same bundle)
    const selectedBundles = new Set(
      selectedInvoices.map((invoice) => invoice.bundleId),
    );
    if (selectedStatuses.size === 1) {
      if (selectedStatuses.has(INVOICE_SENT_SUCCESS)) {
        setCanSend(false);
        setCanResend(true);
        if (selectedInvoices.length === 1) {
          const selectedInvoice = selectedInvoices[0];
          const hasExistingCreditInvoice =
            !!selectedInvoice.bundleId &&
            invoices.some(
              (invoice) =>
                invoice.originalInvoiceNumber === selectedInvoice.invoiceNumber,
            );
          if (hasExistingCreditInvoice) {
            setCanCreateCreditInvoice(false);
          } else {
            setCanCreateCreditInvoice(true);
          }
        } else {
          setCanCreateCreditInvoice(false);
        }
      } else if (
        selectedStatuses.has(INVOICE_DRAFT) ||
        selectedStatuses.has(INVOICE_EDITED) ||
        selectedStatuses.has(INVOICE_READY) ||
        selectedStatuses.has(INVOICE_SENT_FAILED)
      ) {
        setCanSend(true);
        setCanResend(false);
        setCanCreateCreditInvoice(false);
      } else {
        setCanSend(false);
        setCanResend(false);
        setCanCreateCreditInvoice(false);
      }
    } else if (
      selectedInvoices.every((invoice) => invoice.bundleId) &&
      selectedBundles.size === 1
    ) {
      setCanSend(true);
      setCanResend(false);
      setCanCreateCreditInvoice(false);
    } else {
      setCanSend(false);
      setCanResend(false);
      setCanCreateCreditInvoice(false);
    }
  }, [selectedStatuses, selectedInvoices, invoices]);

  useEffect(() => {
    // If selected invoices have different waves or
    // wave is undefined, no wave operation is allowed
    if (
      selectedWaves.size === 1 &&
      !selectedWaves.has(undefined) &&
      !selectedStatuses.has(INVOICE_SENT_SUCCESS)
    ) {
      if (selectedWaves.has(INVOICE_WAVE_ONE)) {
        setCanMarkFirstWave(false);
        setCanMarkSecondWave(true);
      } else if (selectedWaves.has(INVOICE_WAVE_TWO)) {
        setCanMarkFirstWave(true);
        setCanMarkSecondWave(false);
      }
    } else {
      setCanMarkFirstWave(false);
      setCanMarkSecondWave(false);
    }
  }, [selectedWaves, selectedStatuses]);

  useEffect(() => {
    const statuses = new Set(
      selectedInvoices.map((invoice) => invoice.invoiceStatus),
    );
    const waves = new Set(
      selectedInvoices.map((invoice) => invoice.invoiceWave),
    );
    setSelectedStatuses(statuses);
    setSelectedWaves(waves);
  }, [selectedInvoices]);

  return (
    <ActionBanner isOpen={selectedInvoices.length > 0}>
      {isUserAllowedToEdit ? (
        <Box>
          <ActionButton variant="outlined" onClick={previewSelected}>
            {t("preview")}
          </ActionButton>
          {canCreateCreditInvoice ? (
            <ActionButton
              variant="outlined"
              onClick={() => openCreditInvoiceDialog(selectedInvoices[0].id)}
            >
              {t("createCreditInvoice")}
            </ActionButton>
          ) : null}

          {canMarkFirstWave || canMarkSecondWave ? (
            <ActionButtonGroup>
              {!!canMarkFirstWave ? (
                <GroupButton onClick={() => markWave(INVOICE_WAVE_ONE)}>
                  {t("markFirstWave")}
                </GroupButton>
              ) : !!canMarkSecondWave ? (
                <GroupButton onClick={() => markWave(INVOICE_WAVE_TWO)}>
                  {t("markSecondWave")}
                </GroupButton>
              ) : null}
              {canSend ? (
                <ActionButton variant="outlined" onClick={askForConfirmation}>
                  {t("send")}
                </ActionButton>
              ) : canResend ? (
                <ActionButton variant="outlined" onClick={askForConfirmation}>
                  {t("resend")}
                </ActionButton>
              ) : (
                <GroupButton disabled={!canSend}>{t("send")}</GroupButton>
              )}
            </ActionButtonGroup>
          ) : canSend ? (
            <ActionButton variant="outlined" onClick={askForConfirmation}>
              {t("send")}
            </ActionButton>
          ) : canResend ? (
            <ActionButton variant="outlined" onClick={askForConfirmation}>
              {t("resend")}
            </ActionButton>
          ) : null}
        </Box>
      ) : (
        <Box>
          <ActionButton variant="outlined" onClick={previewSelected}>
            {t("preview")}
          </ActionButton>
        </Box>
      )}
      <ConfirmationDialog
        title={t("confirmSending")}
        contentText={t("confirmSendingMultipleInfo")}
        cancelText={t("common:cancel")}
        confirmText={t("send")}
        isOpen={isConfirmationDialogOpen}
        onConfirm={sendSelectedInvoices}
        onCancel={() => setIsConfirmationDialogOpen(false)}
      />
    </ActionBanner>
  );
};
